import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Calendar,
  Users,
  TrendingUp,
  Euro,
  Clock,
  CheckCircle,
  XCircle,
  ArrowUpRight,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalBookings: 0,
    todayBookings: 0,
    totalRevenue: 0,
    pendingBookings: 0,
  });
  const [recentBookings, setRecentBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("adminToken");
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };
  };

  const fetchDashboardData = async () => {
    try {
      const headers = getAuthHeaders();
      const [statsRes, bookingsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/stats`, { headers }),
        fetch(`${API_URL}/api/admin/bookings?limit=5`, { headers }),
      ]);

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }

      if (bookingsRes.ok) {
        const bookingsData = await bookingsRes.json();
        setRecentBookings(bookingsData.bookings || []);
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: "Total Bookings",
      value: stats.totalBookings,
      icon: Calendar,
      color: "bg-blue-500",
      change: "+12%",
    },
    {
      title: "Today's Bookings",
      value: stats.todayBookings,
      icon: Clock,
      color: "bg-green-500",
      change: "+5%",
    },
    {
      title: "Total Revenue",
      value: `€${stats.totalRevenue.toLocaleString()}`,
      icon: Euro,
      color: "bg-secondary-500",
      change: "+18%",
    },
    {
      title: "Pending",
      value: stats.pendingBookings,
      icon: Users,
      color: "bg-purple-500",
      change: "-3%",
    },
  ];

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-6">
        <h1 className="font-heading font-bold text-2xl text-slate-900">
          Dashboard
        </h1>
        <p className="text-slate-600">
          Welcome back! Here's what's happening today.
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {statCards.map((stat, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className={`p-3 rounded-xl ${stat.color}`}>
                    <stat.icon className="w-6 h-6 text-white" />
                  </div>
                  <span className="flex items-center text-sm text-green-600 font-medium">
                    {stat.change}
                    <ArrowUpRight className="w-4 h-4 ml-1" />
                  </span>
                </div>
                <p className="text-sm text-slate-500 mb-1">{stat.title}</p>
                <p className="font-heading font-bold text-2xl text-slate-900">
                  {stat.value}
                </p>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Recent Bookings */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading">Recent Bookings</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-slate-500">Loading...</div>
          ) : recentBookings.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              No bookings yet
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-sm text-slate-500 border-b">
                    <th className="pb-3 font-medium">Booking ID</th>
                    <th className="pb-3 font-medium">Customer</th>
                    <th className="pb-3 font-medium">Route</th>
                    <th className="pb-3 font-medium">Date</th>
                    <th className="pb-3 font-medium">Amount</th>
                    <th className="pb-3 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {recentBookings.map((booking) => (
                    <tr key={booking.id} className="text-sm">
                      <td className="py-4 font-medium text-primary-600">
                        {booking.booking_id}
                      </td>
                      <td className="py-4 text-slate-900">
                        {booking.customer_name}
                      </td>
                      <td className="py-4 text-slate-600">
                        {booking.pickup_location?.split(" ")[0]} →{" "}
                        {booking.dropoff_location?.split(" ")[0]}
                      </td>
                      <td className="py-4 text-slate-600">
                        {new Date(booking.pickup_datetime).toLocaleDateString()}
                      </td>
                      <td className="py-4 font-medium text-slate-900">
                        €{booking.total_price}
                      </td>
                      <td className="py-4">
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                          <CheckCircle className="w-3 h-3" />
                          Confirmed
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminDashboard;
